/**
 * File: hooks/usePermissions.js
 * ==============================
 * Hook for checking user permissions
 */

import { useCallback, useMemo } from 'react';
import { useAuth } from '../context/AuthContext.jsx';

/**
 * Role-based permission defaults
 */
const ROLE_PERMISSIONS = {
  owner: ['view', 'edit', 'delete', 'manage_users', 'approve_estimates', 'financials', 'documents', 'timeline', 'tasks', 'neighborhood'],
  co_owner: ['view', 'edit', 'manage_users', 'approve_estimates', 'financials', 'documents', 'timeline', 'tasks', 'neighborhood'],
  family_member: ['view', 'edit', 'approve_estimates', 'documents', 'timeline', 'tasks', 'neighborhood'],
  tenant: ['view', 'tasks', 'timeline'],
  service_provider: ['view_assigned_tasks']
};

export function usePermissions() {
  const { user } = useAuth();

  /**
   * Check if user has a specific permission for a property
   */
  const hasPermission = useCallback((permission, propertyId) => {
    if (!user) return false;

    // Check direct property access
    const propertyAccess = user.propertyAccess?.find(
      pa => String(pa.propertyId) === String(propertyId)
    );

    if (propertyAccess) {
      const perms = propertyAccess.permissions || ROLE_PERMISSIONS[propertyAccess.role] || [];
      return perms.includes(permission);
    }

    // Check portfolio access
    const portfolioGrant = user.portfolioGrants?.find(pg => pg.status === 'active');
    if (portfolioGrant) {
      const perms = ROLE_PERMISSIONS[portfolioGrant.role] || [];
      return perms.includes(permission);
    }

    return false;
  }, [user]);

  /**
   * Get user's role for a property
   */
  const getRole = useCallback((propertyId) => {
    if (!user) return null;

    const propertyAccess = user.propertyAccess?.find(
      pa => String(pa.propertyId) === String(propertyId)
    );

    if (propertyAccess) {
      return propertyAccess.role;
    }

    const portfolioGrant = user.portfolioGrants?.find(pg => pg.status === 'active');
    if (portfolioGrant) {
      return portfolioGrant.role;
    }

    return null;
  }, [user]);

  /**
   * Check if user is a service provider
   */
  const isServiceProvider = useMemo(() => {
    return user?.isServiceProvider || false;
  }, [user]);

  /**
   * Check if user can manage users for a property
   */
  const canManageUsers = useCallback((propertyId) => {
    return hasPermission('manage_users', propertyId);
  }, [hasPermission]);

  /**
   * Check if user can see financials for a property
   */
  const canViewFinancials = useCallback((propertyId) => {
    return hasPermission('financials', propertyId);
  }, [hasPermission]);

  /**
   * Check if user can delete a property
   */
  const canDeleteProperty = useCallback((propertyId) => {
    return hasPermission('delete', propertyId);
  }, [hasPermission]);

  /**
   * Check if user can invite others at portfolio level
   */
  const canInviteAtPortfolioLevel = useMemo(() => {
    if (!user?.propertyAccess) return false;
    return user.propertyAccess.some(
      pa => ['owner', 'co_owner'].includes(pa.role)
    );
  }, [user]);

  return {
    hasPermission,
    getRole,
    isServiceProvider,
    canManageUsers,
    canViewFinancials,
    canDeleteProperty,
    canInviteAtPortfolioLevel,
    user
  };
}

export default usePermissions;
